import { useEffect, useState } from "react";
import { api } from "@/lib/api";

/**
 * Phase 4.2 — Coronation Banner
 *
 * Edge-to-edge brutalist system announcement injected at the absolute top
 * of every dashboard surface during the first 7 days of a new calendar
 * month, AFTER a successful month-end settlement was archived to
 * `past_winners`. Disappears automatically when:
 *   • no settlement exists yet for the prior month
 *   • the grace window (day-of-month <= 7) has expired
 *   • the API responds with `available: false`
 *
 * Visual rules (Tactical Brutalism):
 *   • solid neon-cyan slab, black text
 *   • zero rounded corners (sharp edge)
 *   • `border-y-4` black bars top/bottom for inverted-alert framing
 *   • mono uppercase tracking-wider
 *   • 🏆 emoji crown bookends per spec
 *   • subtle flashing animation via opacity ping (CSS `@keyframes` already
 *     in index.css? — fall back to inline keyframe class so component is
 *     self-contained)
 */
export default function CoronationBanner({ leagueId }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!leagueId) return;
    let alive = true;
    api.getLatestSettlement(leagueId)
      .then((d) => { if (alive) setData(d); })
      .catch(() => { if (alive) setData({ available: false }); });
    return () => { alive = false; };
  }, [leagueId]);

  if (!data || !data.available) return null;

  const winner = (data.winner_name || "").toUpperCase();
  const monthName = (data.month_name || "").toUpperCase();
  const prize = (data.league_prize || "—").toUpperCase();
  const days = data.days_remaining ?? null;

  return (
    <div
      className="coronation-banner-root w-full"
      data-testid="coronation-banner"
      role="status"
      aria-live="polite"
    >
      <style>{`
        @keyframes coronation-pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.78; }
        }
        .coronation-banner-root {
          background: var(--neon-blue, #00e5ff);
          color: #000;
          border-top: 4px solid #000;
          border-bottom: 4px solid #000;
          animation: coronation-pulse 2.4s ease-in-out infinite;
        }
        @media (prefers-reduced-motion: reduce) {
          .coronation-banner-root { animation: none; }
        }
      `}</style>
      <div className="max-w-[1600px] mx-auto px-4 md:px-12 py-3 md:py-4 flex flex-col md:flex-row items-center justify-between gap-2 md:gap-6 font-mono uppercase tracking-wider">
        <div className="text-center md:text-left text-sm sm:text-base md:text-lg font-black break-words">
          <span data-testid="coronation-banner-text">
            🏆 REIGNING CHAMPION: {winner} WON THE {monthName} CUP — PRIZE SECURED: {prize} 🏆
          </span>
        </div>
        {days !== null && (
          <div className="text-xs sm:text-sm font-bold border-2 border-black px-3 py-1 whitespace-nowrap" data-testid="coronation-banner-grace">
            {days === 1 ? "FINAL DAY OF VICTORY LAP" : `${days} DAYS LEFT IN VICTORY LAP`}
          </div>
        )}
      </div>
    </div>
  );
}
